const DATABRICKS_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface ClassificationResult {
  isServicePrincipal: boolean;
  reason: string;
}

export const classifyIdentifier = (
  identifier: string,
  prefixes: string[],
  regexes: string[]
): ClassificationResult => {
  if (DATABRICKS_UUID_RE.test(identifier)) {
    return { isServicePrincipal: true, reason: "matched built-in UUID rule" };
  }
  const matchedPrefix = prefixes.find(p => p && identifier.startsWith(p));
  if (matchedPrefix) {
    return { isServicePrincipal: true, reason: `matched prefix '${matchedPrefix}'` };
  }
  for (const pattern of regexes) {
    if (!pattern) continue;
    try {
      if (new RegExp(pattern).test(identifier)) {
        return { isServicePrincipal: true, reason: `matched regex '${pattern}'` };
      }
    } catch {
      // Invalid regex — skip
    }
  }
  return { isServicePrincipal: false, reason: "no rule matched" };
};

export const isValidRegex = (value: string): boolean => {
  try {
    new RegExp(value);
    return true;
  } catch {
    return false;
  }
};

interface ParsedSaveError {
  message: string;
  offendingRegexes: Set<string>;
}

export const parseSaveError = (err: unknown, regexes: string[]): ParsedSaveError => {
  // The shared axios interceptor rejects with `response.data.detail` directly,
  // so for FastAPI 422 errors we receive the validation issues array as the thrown value.
  const detail = Array.isArray(err)
    ? err
    : (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  const offendingRegexes = new Set<string>();
  const messages: string[] = [];
  if (Array.isArray(detail)) {
    detail.forEach(item => {
      const loc = (item as { loc?: unknown[] })?.loc;
      if (Array.isArray(loc) && loc[1] === "regexes" && typeof loc[2] === "number") {
        const idx = loc[2];
        if (regexes[idx] !== undefined) offendingRegexes.add(regexes[idx]);
      }
      const msg = (item as { msg?: string })?.msg;
      if (msg) messages.push(msg);
    });
  } else if (typeof err === "string") {
    messages.push(err);
  }
  const message = messages.length > 0 ? messages.join("; ") : "Failed to save classification rules";
  return { message, offendingRegexes };
};

// Order-insensitive equality for string rule sets. Lets us compare current
// state against the loaded snapshot without flagging dirty on reorder.
export const sameRuleSet = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  for (let i = 0; i < sortedA.length; i++) {
    if (sortedA[i] !== sortedB[i]) return false;
  }
  return true;
};
